import { APP_BAR_HEIGHT, getGlobals } from '~/src/UI/Theme/mui'

import { SHORT_LANDSCAPE_MEDIA_QUERY } from '../Lib/Utils'

// Height is what identifies a phone in landscape: the tallest one lands around
// 448px (Pixel 9 Pro XL, 998x448) while the shortest tablet is the iPad mini at
// 1133x744, so this cap separates them with room to spare.
const LANDSCAPE_PHONE = 'screen and (orientation: landscape) and (max-height: 499.95px)'

// The second condition on each landscape clause only exists to keep a short
// desktop window from picking up the phone layout. `pointer: coarse` covers
// every phone whatever its width; the width cap covers a phone reporting a fine
// pointer because a mouse or stylus is paired, and 1024px clears the widest
// phone in landscape without reaching the width of a docked desktop window.
export const MOBILE_LAYOUT_MEDIA_QUERY = [
  '@media screen and (max-width: 599.95px)',
  `${LANDSCAPE_PHONE} and (pointer: coarse)`,
  `${LANDSCAPE_PHONE} and (max-width: 1023.95px)`,
].join(', ')

export const MOBILE_LANDSCAPE_MEDIA_QUERY = [
  `@media ${LANDSCAPE_PHONE} and (pointer: coarse)`,
  `${LANDSCAPE_PHONE} and (max-width: 1023.95px)`,
].join(', ')

export const globals = theme => ({
  ...getGlobals(theme),
  '.aroya': {
    overflow: 'hidden',
  },
  '.aroya *': {
    boxSizing: 'border-box',
  },
  '.aroya #root': {
    minWidth: '100vw',
    maxWidth: '100vw',
  },
  '.aroya ::-webkit-scrollbar': {
    width: 6,
    height: 6,
    backgroundColor: `var(--scroll-bg, ${theme.palette.primary.active})`,
    borderRadius: 3,
  },
  '.aroya ::-webkit-scrollbar-thumb': {
    backgroundColor: `var(--scroll-fg, ${theme.palette.primary.disabled})`,
    borderRadius: 3,
    border: 'none !important',
  },
  '.aroya a:not([class])': {
    color: 'inherit',
  },
  '.aroya a:not(.aroya__link-underline)': {
    textDecoration: 'none',
  },
  '.aroya body': {
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    fontWeight: 400,
    fontFamily: theme.typography.fontFamily,
  },
  '.center-all': {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    alignContent: 'center',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  '@keyframes aroya-fade-in': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  '.fade-in': {
    opacity: 0,
    animationDelay: '800ms',
    animationDuration: '150ms',
    animationFillMode: 'forwards',
    animationIterationCount: 1,
    animationName: 'aroya-fade-in',
    animationTimingFunction: 'ease-in-out',
  },
})

export default (theme, classes = {}) => ({
  zIndex: 1,
  position: 'relative',
  minHeight: '100dvh',
  maxHeight: '100dvh',
  minWidth: '100vw',
  maxWidth: '100vw',
  display: 'grid',
  gridTemplateAreas: [
    '"banner banner banner"',
    '"nav appBar drawer"',
    '"nav content drawer"',
    '"nav footer drawer"',
  ].join('\n'),
  gridTemplateColumns: '84px minmax(0, 1fr) minmax(0, max-content)',
  gridTemplateRows: `auto ${APP_BAR_HEIGHT} 1fr auto`,
  overflow: 'clip',
  '&:before': {
    transition: 'backdrop-filter 250ms ease-in-out',
  },
  [`& > .${classes.appBar}`]: {
    transition: 'filter 250ms ease-in-out',
    paddingTop: 0,
  },
  [`@media screen and (max-height: 767px) and (min-width: ${theme.breakpoints.values.md}px)`]: {
    [`& .${classes.appBar}`]: {
      position: 'fixed',
      top: 0,
      left: 84,
      width: 'calc(100vw - 84px)',
    },
    [`& .${classes.nav}`]: {
      height: '100dvh',
    },
    [`&:has(#aroya-subscription-banner) .${classes.nav}`]: {
      height: 'calc(100dvh - 1.75rem)',
    },
  },
  [`&.${classes.noScroll} .${classes.content}`]: {
    overflow: 'hidden',
  },
  '&.chat': {
    [`& .${classes.appBar}`]: {
      backgroundColor: 'transparent',
    },
    [`& .${classes.content}`]: {
      gridColumn: 'content-start / content-end',
      gridRow: 'appBar-start / content-end',
      maxHeight: 'calc(100dvh - 24px)',
      padding: 0,
      overflow: 'hidden',
    },
  },
  [`&.${classes.fullscreen}`]: {
    maxHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  [`&.${classes.fullscreen} .${classes.content}`]: {
    padding: theme.spacing(0),
    overflow: 'hidden',
    maxHeight: '100dvh',
  },
  [`&.${classes.fullscreen} .${classes.footer}`]: {
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    bottom: 0,
  },
  [`&.${classes.fullscreen} .${classes.debugInfo}`]: {
    whiteSpace: 'normal',
  },
  [`& ~ .${classes.modalRoot}`]: {
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: theme.zIndex?.modal ?? 1300,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    '& > *': {
      pointerEvents: 'auto',
    },
  },
  [`& .${classes.mainHeader}`]: {
    fontSize: '4rem',
  },
  [`& .${classes.writeProgress}`]: {
    position: 'fixed',
    zIndex: 1400,
    bottom: 24,
    left: 24,
    color: theme.palette.error.main,
  },
  [`& .${classes.offlineIndicator}`]: {
    position: 'fixed',
    right: '.5em',
    top: '.5em',
    color: theme.palette.error.main,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.menuToggle}`]: {
    paddingTop: theme.spacing(),
  },
  [`& .${classes.hide}`]: {
    display: 'none',
  },
  [`& .${classes.nav}`]: {
    gridArea: 'nav',
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.content}`]: {
    gridArea: 'content',
    overflowY: 'auto',
    backgroundColor: 'transparent',
    padding: theme.spacing(0, 3, 3),
    width: '100%',
    maxHeight: `calc(100dvh - var(--app-bar-height, ${APP_BAR_HEIGHT}) - 24px)`,
    '#wrapper:has(#drawerRoot:not(:empty)) &': {
      paddingRight: theme.spacing(0),
    },
    [SHORT_LANDSCAPE_MEDIA_QUERY]: {
      '& > :first-child:last-child:not(.aroya__no-auto-height)': {
        maxHeight: `calc(45rem - var(--app-bar-height, ${APP_BAR_HEIGHT}) - 3rem) !important`,
      },
    },
    '@media (min-height: 45.1rem), (orientation: portrait)': {
      '& > :first-child:last-child:not(.aroya__no-auto-height)': {
        maxHeight: `calc(100dvh - var(--app-bar-height, ${APP_BAR_HEIGHT}) - 3rem) !important`,
      },
    },
    '& > :first-child:last-child:not(.aroya__no-auto-height)': {
      overflowY: 'auto !important',
      scrollbarGutter: 'stable',
    },
    '@media print': {
      padding: 0,
    },
  },
  [`& .${classes.errorHeading}`]: {
    marginTop: theme.spacing(),
    flex: '0 0 100%',
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.errorIcon}`]: {
    color: theme.palette.warning.main,
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.maintenanceMessage}`]: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: '200px',
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.footer}`]: {
    gridArea: 'footer',
    width: '100%',
    height: 24,
    display: 'flex',
    flexDirection: 'row-reverse',
    gap: '2ch',
    paddingInline: '1ch',
  },
  [`& .${classes.debugInfo}`]: {
    fontSize: 12,
    pointerEvents: 'none',
    '@media print': {
      display: 'none',
    },
  },
  [`& .${classes.drawerRoot}`]: {
    gridArea: 'drawer',
    zIndex: 1202,
    flex: '0 0 4rem',
    width: '3rem',
    transition: theme.transitions.create(['width'], {
      duration: theme.transitions.duration.leavingScreen,
      easing: theme.transitions.easing.easeInOut,
    }),
    '&:empty': {
      width: 0,
      transition: theme.transitions.create(['width'], {
        duration: theme.transitions.duration.enteringScreen,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    '#wrapper.aroya__drawer__open &': {
      width: 'max-content',
    },
    '&:has(.aroya__drawer__open)': {
      width: 'max-content',
    },
    '& .MuiDrawer-paper.aroya__drawer__open .aroya__drawer__content': {
      opacity: 1,
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.enteringScreen,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    '& .MuiDrawer-paper .aroya__drawer__content': {
      opacity: 0,
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.leavingScreen,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
    '& .MuiDrawer-paper': {
      pointerEvents: 'auto',
    },
    '@media screen and (max-width: 89.99rem)': {
      '& .MuiDrawer-root': {
        position: 'absolute',
        right: 0,
        height: '100%',
        '.aroya-subscription-banner &': {
          height: 'calc(100% - 1.75rem)',
        },
      },
    },
    '@media print': {
      display: 'none',
    },
  },
  [MOBILE_LAYOUT_MEDIA_QUERY]: {
    '--mobile-nav-height': '5.25rem',
    gridTemplateAreas: ['"banner"', '"appBar"', '"content"', '"footer"', '"nav"'].join('\n'),
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateRows: 'auto auto minmax(0, 1fr) 0 var(--mobile-nav-height)',
    '&.chat': {
      [`& .${classes.content}`]: {
        gridRow: 'content',
        maxHeight: 'none',
        padding: 0,
      },
    },
    [`& .${classes.appBar}`]: {
      position: 'static',
      top: 'auto',
      left: 'auto',
      width: '100%',
      minWidth: 0,
      padding: 0,
    },
    [`& .${classes.nav}`]: {
      position: 'fixed',
      right: 0,
      bottom: 0,
      left: 0,
      width: '100%',
      height: 'var(--mobile-nav-height)',
    },
    [`& .${classes.content}`]: {
      minWidth: 0,
      maxHeight: 'none',
      overflowX: 'hidden',
      padding: theme.spacing(0, 1, 1),
      scrollbarGutter: 'auto',
      '& > :first-child:last-child:not(.aroya__no-auto-height)': {
        maxHeight: 'none !important',
        overflowY: 'visible !important',
        scrollbarGutter: 'auto',
      },
      '#wrapper:has(#drawerRoot:not(:empty)) &': {
        paddingRight: theme.spacing(),
      },
    },
    [`& .${classes.footer}`]: {
      display: 'none',
    },
    [`& .${classes.drawerRoot}`]: {
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 'var(--mobile-nav-height)',
      width: 0,
      '& .MuiDrawer-docked .MuiDrawer-paper:not(.aroya__drawer__open)': {
        pointerEvents: 'none',
      },
      '& .MuiDrawer-paper.aroya__drawer__open > :first-child': {
        width: '100%',
      },
    },
  },
  // MobileNav hides the bar in landscape, so no room is reserved for it.
  [MOBILE_LANDSCAPE_MEDIA_QUERY]: {
    '--mobile-nav-height': '0px',
  },
})
